import React, { useState, useEffect, useImperativeHandle, forwardRef, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ServiceProvider from "@client.services/provider";
import { makeUsers } from "@client.pages/Settings/reducer";
import { makeProfile } from "@client.pages/Login/reducer";
import { Label } from "@client.components/Modal";
import Dropdown from "@client.components/Dropdown";
import { Radio } from "@client.components/Radio";
import Loader from "@client.components/Loader";
import UserRoles from "@client.enums/userRoles";
import "./DeleteUserModal.scss";

const DELETION_MODES = {
  SIMPLE: "simple",
  HIERARCHY: "hierarchy",
  REPLACE_EXISTING: "replace_existing",
};

const ROLE_WEIGHTS = {
  [UserRoles.SuperAdmin]: 4,
  [UserRoles.CXO]: 3,
  [UserRoles.Executive]: 2,
  [UserRoles.Manager]: 1,
  [UserRoles.Viewer]: 0,
};

const DeleteUserModal = forwardRef(({ user, onDelete }, ref) => {
  const { t } = useTranslation();
  const users = useSelector(makeUsers);
  const profile = useSelector(makeProfile);
  const [deletionMode, setDeletionMode] = useState(DELETION_MODES.SIMPLE);
  const [descendants, setDescendants] = useState(null);
  const [loadingDescendants, setLoadingDescendants] = useState(false);
  const [selectedReplacementUserId, setSelectedReplacementUserId] = useState("");
  const [replacementUserError, setReplacementUserError] = useState(null);

  useEffect(() => {
    const fetchDescendants = async () => {
      setLoadingDescendants(true);
      try {
        const response = await ServiceProvider.User.getDescendants(user.id);
        setDescendants(response);
      } catch (error) {
        console.error("Failed to fetch descendants:", error);
        setDescendants({ count: 0, descendants: [] });
      } finally {
        setLoadingDescendants(false);
      }
    };

    if (user.id) {
      fetchDescendants();
    }
  }, [user.id]);

  const hasDescendants = descendants && descendants.count > 0;

  const availableUsers = useMemo(() => {
    if (!users.data) return [];

    const currentUser = users.data.find((u) => u.id === profile.userId);
    const userRoleWeight = ROLE_WEIGHTS[user.role] || 0;
    const currentUserRoleWeight = currentUser
      ? ROLE_WEIGHTS[currentUser.role] || 0
      : 4;

    return users.data
      .filter((u) => {
        const userRole = ROLE_WEIGHTS[u.role] || 0;
        return (
          u.id !== user.id &&
          !descendants?.descendants?.some((d) => d.id === u.id) &&
          userRole >= userRoleWeight &&
          userRole <= currentUserRoleWeight
        );
      })
      .map((u) => ({
        value: u.id,
        label: `${u.firstName} ${u.lastName} (${u.email})`,
      }));
  }, [users.data, user.id, user.role, profile.userId, descendants]);

  const handleDelete = async () => {
    try {
      const deleteOptions = {
        userId: user.id,
        deletionMode: hasDescendants ? deletionMode : DELETION_MODES.SIMPLE,
      };

      if (hasDescendants) {
        if (deletionMode === DELETION_MODES.REPLACE_EXISTING) {
          if (!selectedReplacementUserId) {
            setReplacementUserError(
              t("components.UserManagementContent.selectReplacementUser.validation")
            );
            return Promise.resolve({ error: true });
          }
          setReplacementUserError(null);
          deleteOptions.replacementUserId = selectedReplacementUserId;
        }
      }

      const result = await onDelete(deleteOptions);
      return result;
    } catch (error) {
      console.error("Delete failed:", error);
      return Promise.resolve({ error: true });
    }
  };

  useImperativeHandle(ref, () => ({
    handleDelete,
  }));

  if (loadingDescendants) {
    return (
      <div className="delete-user-modal">
        <Loader loading={true} />
      </div>
    );
  }

  if (!hasDescendants) {
    return (
      <div className="delete-user-modal">
        <span className="delete-confirmation">
          {t("components.UserManagementContent.deleteConfirmation", {
            email: user.email,
          })}
        </span>
      </div>
    );
  }

  return (
    <div className="delete-user-modal">
      <div className="delete-warning">
        <p>
          {t("components.UserManagementContent.deleteWarning", {
            email: user.email,
            count: descendants.count,
          })}
        </p>
      </div>

      <div className="deletion-mode-selection">
        <Radio
          name="deletionMode"
          value={deletionMode === DELETION_MODES.HIERARCHY}
          onChange={() => setDeletionMode(DELETION_MODES.HIERARCHY)}
          label={t("components.UserManagementContent.deleteWithHierarchy")}
          className="deletion-mode-option"
        />

        <Radio
          name="deletionMode"
          value={deletionMode === DELETION_MODES.REPLACE_EXISTING}
          onChange={() => setDeletionMode(DELETION_MODES.REPLACE_EXISTING)}
          label={t("components.UserManagementContent.replaceWithExisting")}
          className="deletion-mode-option"
        />
      </div>

      {deletionMode === DELETION_MODES.REPLACE_EXISTING && (
        <div className="replacement-selection">
          <Label term="components.UserManagementContent.selectReplacementUser" />
          <Dropdown
            id="replacement-user"
            items={availableUsers}
            valueKey="value"
            labelKey="label"
            placeholder={t("components.UserManagementContent.selectUser")}
            defaultValue={selectedReplacementUserId || undefined}
            error={replacementUserError ? [replacementUserError] : null}
            onSelect={(value) => {
              setSelectedReplacementUserId(value);
              setReplacementUserError(null);
            }}
          />
        </div>
      )}
    </div>
  );
});

DeleteUserModal.displayName = "DeleteUserModal";

DeleteUserModal.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    role: PropTypes.string,
  }).isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default DeleteUserModal;

